import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import {
  Order,
  OrderPaymentStatusEnum,
  OrderTicket,
  RefundReason,
  RefundRequestStatus,
} from "@models/order";
import { AdminTasksService } from "@services/admin-tasks.service";
import { SnackBarService } from "@services/snack-bar.service";
import { RefundRequestModalComponent } from "@shared/modals/refund-request-modal/refund-request-modal.component";
import { ImgFallbackDirective } from "@shared/directives/img-fallback/img-fallback.directive";
import { PaymentMethodPipe } from "@shared/pipes/payment-method.pipe";
import { OrderPaymentStatusPipe } from "@shared/pipes/order-payment-status.pipe";
import { OrderPaymentStatusClassPipe } from "@shared/pipes/order-payment-status-class.pipe";
import { snakeCase } from "lodash-es";
import { DateTime } from "luxon";
import { catchError, EMPTY, filter, finalize, switchMap, tap } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { LoadingComponent } from "../../../../../shared/components/loading/loading.component";
import { UtilsService } from "../../../../../utils/utils.service";
import { TicketCardComponent } from "./ticket-card/ticket-card.component";
@Component({
  selector: "app-order-card",
  imports: [
    CommonModule,
    ButtonComponent,
    PaymentMethodPipe,
    TicketCardComponent,
    ImgFallbackDirective,
    LoadingComponent,
    MatTooltipModule,
    OrderPaymentStatusPipe,
    OrderPaymentStatusClassPipe,
  ],
  templateUrl: "./order-card.component.html",
  styleUrl: "./order-card.component.scss",
})
export class OrderCardComponent {
  @Input({ required: true }) order!: Order;

  showTickets = false;
  loading = false;
  private readonly orderPaymentStatusPipe = new OrderPaymentStatusPipe();

  @Output() reloadTicketsAfterChange = new EventEmitter();
  @Output() openDetailsModalClick = new EventEmitter<Order>();
  @Output() paymentClick = new EventEmitter<Order>();

  orderPaymentStatus = OrderPaymentStatusEnum;

  private readonly dialog = inject(MatDialog);
  private readonly adminTasksService = inject(AdminTasksService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly utilsService = inject(UtilsService);
  private readonly router = inject(Router);

  handleReloadTicketsAfterChange() {
    this.reloadTicketsAfterChange.emit();
  }

  handleOpenModalClick(order: Order) {
    this.openDetailsModalClick.emit(order);
  }

  handlePaymentClick(order: Order) {
    this.paymentClick.emit(order);
  }

  get isPastEvent(): boolean {
    const eventEndDate = this.order.tickets[0]?.event_end_date;
    if (!eventEndDate) {
      return false;
    }

    return this.utilsService.isPast(eventEndDate);
  }

  // Reembolso só é permitido até 48h antes do início do evento.
  get isRefundWindowOpen(): boolean {
    const eventStartDate = this.order.tickets[0]?.event_start_date;
    const eventStart = DateTime.fromISO(eventStartDate ?? "");
    if (!eventStart.isValid) {
      return false;
    }

    return DateTime.now() < eventStart.minus({ hours: 48 });
  }

  get orderStatus() {
    return `status--${snakeCase(this.order.payment_status)}`;
  }

  loadEventImage(event_thumb: string) {
    return `${environment.BLOB_URL}/${event_thumb}`;
  }

  toggleTickets() {
    this.showTickets = !this.showTickets;
  }

  get regularTickets(): OrderTicket[] {
    const filtered = this.order.tickets.filter(
      (t) => !t.space_id || t.transferred_from,
    );
    return this.sortTicketsByUserInfoThenId(filtered);
  }

  get spaceGroups(): {
    spaceId: number;
    title: string;
    tickets: OrderTicket[];
  }[] {
    const groups = new Map<number, OrderTicket[]>();
    for (const t of this.order.tickets) {
      if (!t.space_id || t.transferred_from) continue;
      const arr = groups.get(t.space_id) ?? [];
      arr.push(t);
      groups.set(t.space_id, arr);
    }
    return Array.from(groups.entries()).map(([spaceId, tickets]) => ({
      spaceId,
      title: tickets[0]?.title ?? "Espaço",
      tickets: this.sortTicketsByUserInfoThenId(tickets),
    }));
  }

  private hasTicketUserInfo(ticket: OrderTicket): boolean {
    const hasDocument = !!ticket.user_cpf || !!ticket.user_identity_card;
    return (
      hasDocument &&
      !!ticket.user_name &&
      !!ticket.user_email &&
      !!ticket.user_last_name
    );
  }

  private sortTicketsByUserInfoThenId(tickets: OrderTicket[]): OrderTicket[] {
    return [...tickets].sort((a, b) => {
      const aHasInfo = this.hasTicketUserInfo(a);
      const bHasInfo = this.hasTicketUserInfo(b);
      if (aHasInfo !== bHasInfo) {
        return aHasInfo ? -1 : 1;
      }
      return a.id - b.id;
    });
  }

  get isRefundApproved(): boolean {
    return this.order.refund_request_status === RefundRequestStatus.Approved;
  }

  get isRefundRejected(): boolean {
    return this.order.refund_request_status === RefundRequestStatus.Rejected;
  }

  get isRefundInAnalysis(): boolean {
    return (
      this.hasPendingRefund ||
      this.order.payment_status === OrderPaymentStatusEnum.RefundRequested
    );
  }

  isPaymentFailed(): boolean {
    const status = this.order.payment_status;
    return (
      status === OrderPaymentStatusEnum.Failed ||
      status === OrderPaymentStatusEnum.Refused
    );
  }

  get ticketActionsRestricted(): boolean {
    return (
      this.isPaymentPending() ||
      this.isPaymentFailed() ||
      this.order.payment_status === OrderPaymentStatusEnum.Refunded
    );
  }

  private get hasBackendRefundFlags(): boolean {
    return this.order.refund_available !== undefined;
  }

  get hasPendingRefund(): boolean {
    return this.order.refund_request_status === RefundRequestStatus.Pending;
  }

  canShowRefundButton(): boolean {
    // Recusa é terminal: uma vez recusado, o comprador não pode solicitar de novo naquele pedido.
    // Blindagem no front além do backend (refund_available), inclusive no fallback sem flags.
    if (this.isRefundRejected) {
      return false;
    }

    if (this.hasBackendRefundFlags) {
      return !!this.order.refund_available && !this.hasPendingRefund;
    }

    if (this.hasRemainingPayment && !this.hasTransferredTickets()) {
      return true;
    }

    const status = this.order.payment_status;
    return (
      status === OrderPaymentStatusEnum.Paid && !this.hasTransferredTickets()
    );
  }

  get hasRemainingPayment(): boolean {
    return (this.order.remaining_payment?.amount_cents ?? 0) > 0;
  }

  /** Com reembolso em análise, não oferecer finalizar o restante nem o banner de pendência. */
  get showRemainingPaymentPrompt(): boolean {
    return this.hasRemainingPayment && !this.isRefundInAnalysis && !this.isRefundApproved;
  }

  get paymentStatusLabel(): string {
    if (this.isRefundInAnalysis && !this.isRefundApproved) {
      return "Reembolso em análise";
    }
    if (this.hasRemainingPayment) {
      return "Pagamento parcial";
    }
    return this.orderPaymentStatusPipe.transform(this.order.payment_status);
  }

  get remainingPaymentReais(): number {
    return (this.order.remaining_payment?.amount_cents ?? 0) / 100;
  }

  handleFinalizeRemainingPayment(): void {
    this.snackbarService.openSnackBar(
      "Pedido pendente: conclua o valor restante no checkout ou solicite o estorno abaixo.",
      "Fechar",
      "success",
    );
    this.router.navigate(["/"], {
      queryParams: { resumeOrderId: this.order.id },
    });
  }

  canRequestRefund(): boolean {
    if (this.hasBackendRefundFlags) {
      return this.canShowRefundButton();
    }

    return this.canShowRefundButton() && this.isRefundWindowOpen;
  }

  get refundReasonTooltip(): string {
    const reasons: Record<RefundReason, string> = {
      arrependimento: "Reembolso por arrependimento (até 7 dias da compra).",
      event_changed:
        "O evento teve informações relevantes alteradas (data, horário ou local).",
      event_cancelled: "O evento foi cancelado.",
    };

    const reason = this.order.refund_reason;
    return reason ? reasons[reason] : "";
  }

  hasTransferredTickets(): boolean {
    return this.order.tickets.some((t) => t.transferred_from);
  }

  requestRefund() {
    const dialogRef = this.dialog.open(RefundRequestModalComponent, {
      width: "500px",
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(
          (reason: unknown): reason is string =>
            typeof reason === "string" && !!reason.trim(),
        ),
        switchMap((reason) => {
          this.loading = true;
          const eventTitle = this.order.tickets[0]?.event_title ?? "evento";
          return this.adminTasksService
            .requestRefund(
              this.order.id,
              Number(this.order.total_amount),
              `Solicitação de reembolso do pedido #${this.order.id} referente ao evento ${eventTitle}`,
              reason.trim(),
            )
            .pipe(
              tap(() => this.reloadTicketsAfterChange.emit()),
              catchError((err) => {
                const msg =
                  err?.error?.errors?.[0] ??
                  err?.error?.error ??
                  "Erro ao solicitar reembolso";
                this.snackbarService.openSnackBar(msg, "Fechar", "error");

                if (err?.status === 422) {
                  this.reloadTicketsAfterChange.emit();
                }
                return EMPTY;
              }),
              finalize(() => (this.loading = false)),
            );
        }),
      )
      .subscribe();
  }

  isPaymentPending(): boolean {
    const status = this.order.payment_status?.toLowerCase();
    const PAYMENT_PENDING_STATUS_SET = new Set([
      "waiting",
      "ativa",
      "pendente",
      "pending",
      "recebivel",
      "unpaid",
    ]);

    return PAYMENT_PENDING_STATUS_SET.has(status);
  }
}
